/**
 * Who a query's data belongs to, declared once on its options factory.
 *
 * - `user`: the data exists only for a signed-in account (drafts, responses,
 *   poll history). Hooks wait until there is a user who can fetch.
 * - `viewer`: anyone may read it, but what comes back depends on who asks
 *   (Library posts and comments carry the viewer's liked and saved flags). It
 *   runs signed out too, still keyed by viewer.
 *
 * Either way the account id is the factory's first argument, so the key names
 * it and no two accounts share a cache entry.
 */
export type UserScope = 'user' | 'viewer';

export type UserScopedFactory<TArgs extends unknown[], TOptions> = ((
  userId: string | undefined,
  ...args: TArgs
) => TOptions) & { readonly userScope: UserScope };

/**
 * Declares a query options factory whose data belongs to an account. The
 * factory keeps its signature, so code that calls it directly still works;
 * `useUserScopedQuery` and `useUserScopedKey` read the scope and supply the id.
 */
export function userScoped<TArgs extends unknown[], TOptions>(
  userScope: UserScope,
  factory: (userId: string | undefined, ...args: TArgs) => TOptions
): UserScopedFactory<TArgs, TOptions> {
  return Object.assign(factory, { userScope });
}
